import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, Send, Undo2, Loader2, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { pollJobUntilComplete } from '../../../services/aiJobService';
import AiCostBadge from '../../ai/AiCostBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface AIEditChatProps {
  isOpen: boolean;
  onClose: () => void;
  /** HTML atual da página (documento completo) — fallback estático */
  currentHtml: string;
  /** Getter do HTML ao vivo (reflete edições visuais feitas com o chat aberto) */
  getCurrentHtml?: () => string;
  /** Aplica o novo HTML retornado pela IA (o builder empilha o anterior p/ undo) */
  onApply: (newHtml: string) => void;
  /** Desfaz a última alteração aplicada */
  onUndo: () => void;
  /** Se há algo no histórico para desfazer */
  canUndo: boolean;
}

// Chat "Editar com IA": o usuário descreve a mudança em linguagem natural e a IA
// aplica um patch cirúrgico no HTML da página, preservando o resto. Cada alteração
// pode ser desfeita (histórico no builder). O preview atualiza ao vivo porque o
// builder re-renderiza o iframe com o novo HTML.
const AIEditChat: React.FC<AIEditChatProps> = ({ isOpen, onClose, currentHtml, getCurrentHtml, onApply, onUndo, canUndo }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Espelha isOpen: se o usuário fechar o painel durante um request, NÃO aplicamos
  // a edição por baixo dos panos quando ela retornar.
  const openRef = useRef(isOpen);
  useEffect(() => { openRef.current = isOpen; }, [isOpen]);

  const MAX_INSTRUCTION = 2000;

  const suggestions = [
    t('pageBuilder.aiEdit.s1', 'Deixe o hero mais moderno'),
    t('pageBuilder.aiEdit.s2', 'Troque a cor principal para azul'),
    t('pageBuilder.aiEdit.s3', 'Adicione uma seção de depoimentos'),
    t('pageBuilder.aiEdit.s4', 'Deixe o texto do botão mais persuasivo'),
    t('pageBuilder.aiEdit.s5', 'Aumente o espaçamento entre as seções'),
  ];

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text?: string) => {
    let instruction = (text ?? input).trim();
    if (!instruction || loading) return;
    if (instruction.length > MAX_INSTRUCTION) instruction = instruction.slice(0, MAX_INSTRUCTION);
    const liveHtml = (getCurrentHtml ? getCurrentHtml() : currentHtml) || currentHtml;
    if (!liveHtml || liveHtml.length < 20) {
      toast.error(t('pageBuilder.aiEdit.noHtml', 'Esta página ainda não tem HTML para editar.'));
      return;
    }

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: instruction };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error(t('auth.sessionExpired', 'Sessão expirada. Faça login novamente.')); setLoading(false); return; }

      const resp = await axios.post(
        `${API_URL}/landing-pages/ai/edit-html`,
        { html: liveHtml, instruction, async: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );

      let result: any = resp.data?.data;
      if (resp.data?.async && resp.data?.jobId) {
        result = await pollJobUntilComplete(resp.data.jobId, undefined, 2000, 600000);
      }

      // Se o painel foi fechado durante o request, não altera a página por baixo.
      if (!openRef.current) { setLoading(false); return; }

      if (result?.html && result.changed) {
        onApply(result.html);
        setMessages(prev => [...prev, {
          id: `a-${Date.now()}`, role: 'assistant',
          content: t('pageBuilder.aiEdit.applied', 'Pronto! Apliquei a alteração — veja no preview. Se não gostar, é só desfazer.')
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: `a-${Date.now()}`, role: 'assistant',
          content: result?.text || t('pageBuilder.aiEdit.noChange', 'Não consegui aplicar essa alteração. Tente descrever de outro jeito, sendo mais específico.')
        }]);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, {
        id: `a-${Date.now()}`, role: 'assistant',
        content: t('pageBuilder.aiEdit.error', 'Ocorreu um erro ao editar. Tente novamente em instantes.')
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-14 bottom-0 z-30 w-full max-w-md bg-gray-900 border-l border-gray-800 shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{t('pageBuilder.aiEdit.title', 'Editar com IA')}</h3>
            <p className="text-gray-400 text-xs">{t('pageBuilder.aiEdit.subtitle', 'Descreva a mudança — eu edito a página')}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            title={t('pageBuilder.aiEdit.undo', 'Desfazer última alteração') as string}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Wand2 className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
            <p className="text-gray-300 text-sm mb-1">{t('pageBuilder.aiEdit.emptyTitle', 'Edite a página conversando')}</p>
            <p className="text-gray-500 text-xs mb-4">{t('pageBuilder.aiEdit.emptyHint', 'Ex.: "muda a cor do botão pra azul", "aumenta o título", "adiciona uma seção de FAQ"')}</p>
            <div className="flex flex-col gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-left text-xs text-gray-300 bg-gray-800/70 hover:bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
              m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-200'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-800 text-gray-300 rounded-2xl px-3.5 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('pageBuilder.aiEdit.working', 'Editando a página...')}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-800">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('pageBuilder.aiEdit.placeholder', 'Descreva a alteração...') as string}
            rows={2}
            maxLength={MAX_INSTRUCTION}
            disabled={loading}
            className="flex-1 resize-none bg-gray-950 border border-gray-700 rounded-xl px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-indigo-500"
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:from-indigo-500 hover:to-purple-500 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <AiCostBadge tone="dark" feature="ai-generation" action="landing-page-edit" unitLabel={t('aiCredits.perEdit', 'por alteração')} className="mt-1.5" />
      </div>
    </div>
  );
};

export default AIEditChat;
